import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const API = "https://eleven11-i6v4.onrender.com";
const token = () => localStorage.getItem("token");
const authHeaders = () => ({ Authorization: `Bearer ${token()}` });

// ── Shared helpers ──────────────────────────────────────────────
function timeAgo(date) {
  if (!date) return "—";
  const diff = Math.floor((Date.now() - new Date(date)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const STATUS_COLORS = {
  order_placed:      { bg: "#EEF2FF", color: "#4361C2" },
  payment_confirmed: { bg: "#EEF2FF", color: "#4361C2" },
  packaged:          { bg: "#FFF7E6", color: "#B45309" },
  shipped:           { bg: "#E6F0FF", color: "#1D4ED8" },
  out_for_delivery:  { bg: "#FFF0F0", color: "#DC2626" },
  delivered:         { bg: "#EDFDF4", color: "#15803D" },
};
const STATUS_LABELS = {
  order_placed: "Placed", payment_confirmed: "Confirmed",
  packaged: "Packaged", shipped: "Shipped",
  out_for_delivery: "Out for Delivery", delivered: "Delivered",
};
const FULFILLMENT_STAGES = [
  { key: "order_placed", label: "Order Placed" },
  { key: "payment_confirmed", label: "Payment Confirmed" },
  { key: "packaged", label: "Packaged" },
  { key: "shipped", label: "Shipped" },
  { key: "out_for_delivery", label: "Out for Delivery" },
  { key: "delivered", label: "Delivered" },
];
const EVENT_CONFIG = {
  site_visit:       { label: "Site Visit",   color: "#6366f1", bg: "#eef2ff" },
  product_view:     { label: "Product View", color: "#0ea5e9", bg: "#e0f2fe" },
  add_to_cart:      { label: "Cart Add",     color: "#f59e0b", bg: "#fef3c7" },
  checkout_started: { label: "Checkout",     color: "#f97316", bg: "#fff7ed" },
  order_placed:     { label: "Order",        color: "#10b981", bg: "#d1fae5" },
};
const RETURN_STATUS = {
  pending:    { label: "Pending",    color: "#f59e0b", bg: "#fffbeb" },
  approved:   { label: "Approved",  color: "#3b82f6", bg: "#eff6ff" },
  denied:     { label: "Denied",    color: "#ef4444", bg: "#fef2f2" },
  label_sent: { label: "Label Sent",color: "#8b5cf6", bg: "#f5f3ff" },
  received:   { label: "Received",  color: "#06b6d4", bg: "#ecfeff" },
  credited:   { label: "Credited",  color: "#10b981", bg: "#ecfdf5" },
};

const inputStyle = {
  width: "100%", padding: "12px 14px", border: "1px solid #e5e7eb", borderRadius: 12,
  fontSize: 14, outline: "none", boxSizing: "border-box", background: "#fff",
  fontFamily: "-apple-system, BlinkMacSystemFont, system-ui"
};
const btnStyle = {
  width: "100%", padding: "14px", border: "none", borderRadius: 14,
  background: "#0f0f17", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer"
};

function Badge({ status, map }) {
  const cfg = map[status] || { label: status, color: "#6b7280", bg: "#f3f4f6" };
  return (
    <span style={{ background: cfg.bg, color: cfg.color, padding: "3px 9px", borderRadius: 999, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
      {cfg.label}
    </span>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ background: "#fff", borderRadius: 16, marginBottom: 12, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
      {title && <div style={{ padding: "14px 16px 10px", fontSize: 11, fontWeight: 800, color: "#9ca3af", letterSpacing: 1.5, textTransform: "uppercase", borderBottom: "1px solid #f3f4f6" }}>{title}</div>}
      {children}
    </div>
  );
}

function InfoRow({ label, value, link }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderBottom: "1px solid #f9fafb" }}>
      <span style={{ fontSize: 13, color: "#9ca3af" }}>{label}</span>
      {link
        ? <a href={link} style={{ fontSize: 13, color: "#6366f1", fontWeight: 600, textDecoration: "none" }}>{value}</a>
        : <span style={{ fontSize: 13, color: "#0f0f17", fontWeight: 600, textAlign: "right", maxWidth: "60%" }}>{value}</span>
      }
    </div>
  );
}

// ── DASHBOARD ───────────────────────────────────────────────────
function DashboardTab() {
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [orders, setOrders] = useState([]);
  const [revenue, setRevenue] = useState(0);

  useEffect(() => {
   Promise.all([
      fetch(`${API}/api/analytics/summary`, { headers: authHeaders() }).then(r => r.json()),
      fetch(`${API}/api/analytics/events?limit=15`, { headers: authHeaders() }).then(r => r.json()),
      fetch(`${API}/api/orders`, { headers: authHeaders() }).then(r => r.json()),
    ]).then(([s, e, o]) => {
      setSummary(s);
      setEvents(e.events || []);
      const all = o.orders || [];
      setOrders(all.slice(0, 5));
      setRevenue(all.reduce((sum, x) => sum + Number(x.total || 0), 0));
    }).catch(console.error);
  }, []);

  const metrics = summary ? [
    { label: "Today's Visitors", value: summary.todayVisits,        color: "#6366f1" },
    { label: "Product Views",    value: summary.totalProductViews,  color: "#0ea5e9" },
    { label: "Cart Adds",        value: summary.totalAddToCart,     color: "#f59e0b" },
    { label: "Checkouts",        value: summary.totalCheckouts,     color: "#f97316" },
    { label: "Orders",           value: summary.totalOrders,        color: "#10b981" },
    { label: "Revenue",          value: `$${revenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, color: "#8b5cf6" },
  ] : [];

  return (
    <div style={{ padding: "16px 16px 100px" }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: "#9ca3af" }}>ELEVEN</div>
        <h1 style={{ margin: "4px 0 0", fontSize: 28, fontWeight: 800, letterSpacing: -1, color: "#0f0f17" }}>Dashboard</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#10b981" }} />
          <span style={{ fontSize: 12, color: "#10b981", fontWeight: 700 }}>Live</span>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
        {metrics.map(m => (
          <div key={m.label} style={{ background: "#fff", borderRadius: 16, padding: "16px 14px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: m.color, borderRadius: "16px 16px 0 0" }} />
            <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 700, marginBottom: 6, marginTop: 4 }}>{m.label}</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: m.color, letterSpacing: -1 }}>{m.value}</div>
          </div>
        ))}
      </div>

      <Section title="Recent Orders">
        {orders.length === 0 && <div style={{ padding: "20px 16px", color: "#9ca3af", fontSize: 14, textAlign: "center" }}>No orders yet</div>}
        {orders.map(o => {
          const stage = o.fulfillmentStatus || "order_placed";
          return (
            <div key={o._id} style={{ display: "flex", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid #f9fafb", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0f0f17" }}>{o.orderNumber}</div>
                <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{o.customer?.firstName} {o.customer?.lastName}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <Badge status={stage} map={STATUS_COLORS} />
                <div style={{ fontSize: 14, fontWeight: 800, color: "#0f0f17", marginTop: 3 }}>${Number(o.total || 0).toFixed(2)}</div>
              </div>
            </div>
          );
        })}
      </Section>

      <Section title="Live Activity">
        {events.length === 0 && <div style={{ padding: "20px 16px", color: "#9ca3af", fontSize: 14, textAlign: "center" }}>No activity yet</div>}
        {events.map((ev, i) => {
          const cfg = EVENT_CONFIG[ev.eventType] || { label: ev.eventType, color: "#888", bg: "#f5f5f5" };
          return (
            <div key={ev._id || i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderBottom: "1px solid #f9fafb" }}>
              <span style={{ background: cfg.bg, color: cfg.color, fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 999, whiteSpace: "nowrap" }}>{cfg.label}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
  <div style={{ fontSize: 13, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
    {ev.userName && <span style={{ fontSize: 11, fontWeight: 700, color: "#6366f1", background: "#eef2ff", padding: "1px 6px", borderRadius: 999, marginRight: 6 }}>{ev.userName}</span>}
    {ev.listingTitle || ev.metadata?.orderNumber || ""}
  </div>
  {ev.eventType === "checkout_started" && ev.metadata?.items?.length > 0 && (
    <div style={{ marginTop: 4, display: "flex", flexDirection: "column", gap: 2 }}>
      {ev.metadata.items.map((item, idx) => (
        <div key={idx} style={{ fontSize: 11, color: "#6b7280", display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontWeight: 700, color: "#f97316" }}>×{item.qty}</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180 }}>{item.title}</span>
          <span style={{ fontWeight: 600, color: "#374151", flexShrink: 0 }}>${Number(item.price).toFixed(2)}</span>
        </div>
      ))}
    </div>
  )}
</div>
              <span style={{ fontSize: 11, color: "#9ca3af", flexShrink: 0 }}>{timeAgo(ev.timestamp)}</span>
            </div>
          );
        })}
      </Section>
    </div>
  );
}

// ── ORDERS ──────────────────────────────────────────────────────
function OrdersTab() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [updateForm, setUpdateForm] = useState({ fulfillmentStatus: "", trackingNumber: "", carrier: "", note: "" });

  useEffect(() => {
    fetch(`${API}/api/orders`).then(r => r.json()).then(d => setOrders(d.orders || [])).finally(() => setLoading(false));
  }, []);

  const openOrder = o => {
    setSelected(o);
    setUpdateForm({ fulfillmentStatus: o.fulfillmentStatus || "order_placed", trackingNumber: o.trackingNumber || "", carrier: o.carrier || "", note: "" });
  };

  const pushUpdate = async () => {
    setUpdating(true);
    try {
      const res = await fetch(`${API}/api/orders/${selected._id}/status`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(updateForm) });
      const d = await res.json();
      if (d.success) { setOrders(prev => prev.map(o => o._id === selected._id ? d.order : o)); setSelected(d.order); }
    } finally { setUpdating(false); }
  };

  const filtered = orders.filter(o => {
    const mf = filter === "all" || o.fulfillmentStatus === filter;
    const ms = !search || o.orderNumber?.toLowerCase().includes(search.toLowerCase()) || `${o.customer?.firstName} ${o.customer?.lastName}`.toLowerCase().includes(search.toLowerCase());
    return mf && ms;
  });

  if (selected) return (
    <div style={{ padding: "16px 16px 100px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button onClick={() => setSelected(null)} style={{ background: "#f3f4f6", border: "none", borderRadius: 10, padding: "8px 14px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>← Back</button>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#0f0f17" }}>{selected.orderNumber}</div>
          <div style={{ fontSize: 13, color: "#9ca3af" }}>{selected.customer?.firstName} {selected.customer?.lastName}</div>
        </div>
      </div>

      <Section title="Items">
        {(selected.items || []).map((item, i) => (
          <div key={i} style={{ display: "flex", gap: 12, padding: "12px 16px", borderBottom: "1px solid #f9fafb", alignItems: "center" }}>
            {item.image && <img src={item.image} style={{ width: 48, height: 48, borderRadius: 10, objectFit: "cover" }} alt={item.title} />}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#0f0f17" }}>{item.title}</div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>Qty: {item.qty} · ${Number(item.price).toFixed(2)} each</div>
              {item.selectedCondition && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 999, background: item.selectedCondition === "new" ? "#e0f2fe" : "#f3f4f6", color: item.selectedCondition === "new" ? "#0369a1" : "#6b7280", marginTop: 3, display: "inline-block" }}>
                  {item.selectedCondition === "new" ? "Brand New" : "Used"}
                </span>
              )}
            </div>
          </div>
        ))}
      </Section>

      <Section title="Totals">
        <InfoRow label="Subtotal" value={`$${Number(selected.subtotal || 0).toFixed(2)}`} />
        <InfoRow label="Tax" value={`$${Number(selected.tax || 0).toFixed(2)}`} />
        <InfoRow label="Total" value={`$${Number(selected.total || 0).toFixed(2)}`} />
      </Section>

      {selected.shippingAddress && (
        <Section title="Shipping Address">
          <div style={{ padding: "12px 16px", fontSize: 13, color: "#374151", lineHeight: 1.8 }}>
            {selected.customer?.firstName} {selected.customer?.lastName}<br />
            {selected.shippingAddress.address}<br />
            {selected.shippingAddress.apartment && <>{selected.shippingAddress.apartment}<br /></>}
            {selected.shippingAddress.city}, {selected.shippingAddress.state} {selected.shippingAddress.zip}
          </div>
        </Section>
      )}

      <Section title="Update Fulfillment">
        <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.8 }}>Status</div>
            <select style={inputStyle} value={updateForm.fulfillmentStatus} onChange={e => setUpdateForm(p => ({ ...p, fulfillmentStatus: e.target.value }))}>
              {FULFILLMENT_STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          {[{ label: "Carrier", key: "carrier", placeholder: "UPS, FedEx, USPS..." }, { label: "Tracking #", key: "trackingNumber", placeholder: "Enter tracking number..." }, { label: "Note", key: "note", placeholder: "Internal note..." }].map(f => (
            <div key={f.key}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.8 }}>{f.label}</div>
              <input style={inputStyle} placeholder={f.placeholder} value={updateForm[f.key]} onChange={e => setUpdateForm(p => ({ ...p, [f.key]: e.target.value }))} />
            </div>
          ))}
          <button onClick={pushUpdate} disabled={updating} style={{ ...btnStyle, opacity: updating ? 0.6 : 1 }}>
            {updating ? "Updating..." : "Push Update"}
          </button>
          <button onClick={() => navigate(`/invoice/${selected._id}`)} style={{ ...btnStyle, background: "#f3f4f6", color: "#374151" }}>
            View Invoice →
          </button>
        </div>
      </Section>

      {selected.statusHistory?.length > 0 && (
        <Section title="Status History">
          {[...selected.statusHistory].reverse().map((h, i) => (
            <div key={i} style={{ display: "flex", gap: 10, padding: "10px 16px", borderBottom: "1px solid #f9fafb", alignItems: "flex-start" }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#6366f1", marginTop: 4, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{STATUS_LABELS[h.stage] || h.stage}</div>
                <div style={{ fontSize: 12, color: "#9ca3af" }}>{new Date(h.timestamp).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}{h.note && ` · ${h.note}`}</div>
              </div>
            </div>
          ))}
        </Section>
      )}
    </div>
  );

  return (
    <div style={{ padding: "16px 16px 100px" }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: "#9ca3af" }}>ELEVEN</div>
        <h1 style={{ margin: "4px 0 12px", fontSize: 28, fontWeight: 800, letterSpacing: -1, color: "#0f0f17" }}>Orders</h1>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 14 }}>
          {[
            { label: "Total",   value: orders.length, color: "#0f0f17" },
            { label: "New",     value: orders.filter(o => ["order_placed","payment_confirmed"].includes(o.fulfillmentStatus)).length, color: "#f97316" },
            { label: "Transit", value: orders.filter(o => ["shipped","out_for_delivery"].includes(o.fulfillmentStatus)).length, color: "#1D4ED8" },
            { label: "Done",    value: orders.filter(o => o.fulfillmentStatus === "delivered").length, color: "#15803D" },
          ].map(s => (
            <div key={s.label} style={{ background: "#fff", borderRadius: 12, padding: "10px 8px", textAlign: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
              <div style={{ fontSize: 20, fontWeight: 900, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 700 }}>{s.label}</div>
            </div>
          ))}
        </div>
        <input style={{ ...inputStyle, marginBottom: 10 }} placeholder="Search orders..." value={search} onChange={e => setSearch(e.target.value)} />
        <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4, scrollbarWidth: "none" }}>
          {["all", "order_placed", "packaged", "shipped", "out_for_delivery", "delivered"].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{ flexShrink: 0, padding: "6px 12px", borderRadius: 999, border: "1px solid", borderColor: filter === f ? "#0f0f17" : "#e5e7eb", background: filter === f ? "#0f0f17" : "transparent", color: filter === f ? "#fff" : "#6b7280", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              {f === "all" ? "All" : STATUS_LABELS[f]}
            </button>
          ))}
        </div>
      </div>

      <Section>
        {loading && <div style={{ padding: "30px", textAlign: "center", color: "#9ca3af" }}>Loading...</div>}
        {!loading && filtered.length === 0 && <div style={{ padding: "30px", textAlign: "center", color: "#9ca3af" }}>No orders found</div>}
        {!loading && filtered.map(o => {
          const stage = o.fulfillmentStatus || "order_placed";
          return (
            <div key={o._id} onClick={() => openOrder(o)} style={{ display: "flex", alignItems: "center", padding: "14px 16px", borderBottom: "1px solid #f9fafb", cursor: "pointer", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#0f0f17" }}>{o.orderNumber}</div>
                <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{o.customer?.firstName} {o.customer?.lastName} · {new Date(o.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <Badge status={stage} map={STATUS_COLORS} />
                <div style={{ fontSize: 15, fontWeight: 800, color: "#0f0f17", marginTop: 3 }}>${Number(o.total || 0).toFixed(2)}</div>
              </div>
              <span style={{ color: "#d1d5db", fontSize: 18 }}>›</span>
            </div>
          );
        })}
      </Section>
    </div>
  );
}

// ── LISTINGS ─────────────────────────────────────────────────────
function ListingsTab() {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/listings`).then(r => r.json()).then(d => setListings(Array.isArray(d) ? d : [])).finally(() => setLoading(false));
  }, []);

  const deleteListing = async id => {
    if (!window.confirm("Delete this listing?")) return;
    await fetch(`${API}/api/listings/${id}`, { method: "DELETE" });
    setListings(prev => prev.filter(l => l._id !== id));
    setSelected(null);
  };

  const filtered = listings.filter(l => !search || l.title?.toLowerCase().includes(search.toLowerCase()) || l.partNumber?.toLowerCase().includes(search.toLowerCase()));

  const GRADE_CONFIG = {
    excellent: { label: "Excellent", color: "#15803D", bg: "#EDFDF4" },
    good:      { label: "Good",      color: "#0369a1", bg: "#e0f2fe" },
    fair:      { label: "Fair",      color: "#B45309", bg: "#FFF7E6" },
    poor:      { label: "Poor",      color: "#DC2626", bg: "#FFF0F0" },
  };

  if (selected) {
    const l = selected;
    const nv = l.variants?.find(v => v.condition === "new");
    const uv = l.variants?.find(v => v.condition === "used");
    const salePrice = nv?.price || uv?.price || 0;
    const cost = l.costPrice || 0;
    const margin = cost && salePrice ? Math.round(((salePrice - cost) / salePrice) * 100) : null;
    const stock = (nv?.stock ?? 0) + (uv?.stock ?? 0) || l.unitsAvailable || 0;
    const stockColor = stock <= 0 ? "#DC2626" : stock <= (l.lowStockThreshold || 3) ? "#B45309" : "#15803D";
    const grade = l.conditionGrade ? GRADE_CONFIG[l.conditionGrade] : null;

    return (
      <div style={{ padding: "16px 16px 100px" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <button onClick={() => setSelected(null)} style={{ background: "#f3f4f6", border: "none", borderRadius: 10, padding: "8px 14px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>← Back</button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#0f0f17", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.title}</div>
            <div style={{ fontSize: 12, color: "#9ca3af" }}>{l.listingId}</div>
          </div>
        </div>

        {/* Image + quick stats */}
        {l.images?.[0] && (
          <div style={{ background: "#fff", borderRadius: 20, marginBottom: 12, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
            <div style={{ display: "flex", gap: 0 }}>
              <img src={l.images[0]} alt={l.title} style={{ width: "40%", aspectRatio: "1", objectFit: "contain", background: "#f9fafb", padding: 12, boxSizing: "border-box" }} />
              <div style={{ flex: 1, padding: "16px 14px", display: "flex", flexDirection: "column", justifyContent: "center", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 3 }}>Sale Price</div>
                  <div style={{ fontSize: 24, fontWeight: 900, color: "#0f0f17", letterSpacing: -0.5 }}>${salePrice.toFixed(2)}</div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {nv && <span style={{ fontSize: 11, fontWeight: 700, background: "#e0f2fe", color: "#0369a1", padding: "2px 7px", borderRadius: 999 }}>New ${nv.price}</span>}
                  {uv && <span style={{ fontSize: 11, fontWeight: 700, background: "#f3f4f6", color: "#6b7280", padding: "2px 7px", borderRadius: 999 }}>Used ${uv.price}</span>}
                </div>
                <div style={{ fontSize: 13, fontWeight: 800, color: stockColor }}>{stock} units</div>
                {grade && <span style={{ fontSize: 11, fontWeight: 700, background: grade.bg, color: grade.color, padding: "3px 9px", borderRadius: 999, alignSelf: "flex-start" }}>{grade.label}</span>}
              </div>
            </div>
            {l.images.length > 1 && (
              <div style={{ display: "flex", gap: 8, padding: "0 12px 12px", overflowX: "auto", scrollbarWidth: "none" }}>
                {l.images.slice(1).map((img, i) => (
                  <img key={i} src={img} alt="" style={{ width: 52, height: 52, borderRadius: 10, objectFit: "contain", background: "#f3f4f6", flexShrink: 0 }} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Public Info */}
        <Section title="Product Info">
          <InfoRow label="Title" value={l.title} />
          <InfoRow label="Part #" value={l.partNumber || "—"} />
          <InfoRow label="EID" value={l.eid || "—"} />
          <InfoRow label="Category" value={l.category} />
          <InfoRow label="Condition" value={l.condition === "new" ? "Brand New" : "Used"} />
          <InfoRow label="Manufacturer" value={l.manufacturer || "—"} />
          <InfoRow label="Model" value={l.model || "—"} />
          <InfoRow label="Year" value={l.yearStart && l.yearEnd && l.yearStart !== l.yearEnd ? `${l.yearStart}–${l.yearEnd}` : l.yearStart || "—"} />
          <InfoRow label="Color" value={l.color || "—"} />
          <InfoRow label="Material" value={l.material || "—"} />
          <InfoRow label="Weight" value={l.weightValue ? `${l.weightValue} ${l.weightUnit || ""}` : "—"} />
          {l.badge && <InfoRow label="Badge" value={l.badge} />}
        </Section>

        {/* Visibility */}
        <Section title="Visibility">
          <InfoRow label="Published" value={l.isPublished !== false ? "✓ Live" : "✗ Draft"} />
          <InfoRow label="Featured" value={l.isFeatured ? "✓ Featured" : "No"} />
        </Section>

        {/* Internal — private section */}
        <div style={{ background: "#0f0f17", borderRadius: 20, marginBottom: 12, overflow: "hidden" }}>
          <div style={{ padding: "14px 16px 10px", fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,0.4)", letterSpacing: 1.5, textTransform: "uppercase", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", gap: 8 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
            Internal — Admin Only
          </div>
          {[
            { label: "SKU", value: l.sku || "—" },
            { label: "Cost Price", value: cost ? `$${Number(cost).toFixed(2)}` : "—" },
            { label: "Sale Price", value: salePrice ? `$${salePrice.toFixed(2)}` : "—" },
            { label: "Margin", value: margin !== null ? `${margin}%` : "—" },
            { label: "Supplier", value: l.supplier || "—" },
            { label: "Bin Location", value: l.binLocation || "—" },
            { label: "Low Stock Threshold", value: l.lowStockThreshold ?? 3 },
            { label: "Condition Grade", value: grade ? grade.label : "—" },
            { label: "Alt Part Numbers", value: l.altPartNumbers?.length ? l.altPartNumbers.join(", ") : "—" },
          ].map(row => (
            <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              <span style={{ fontSize: 13, color: "rgba(255,255,255,0.4)" }}>{row.label}</span>
              <span style={{ fontSize: 13, color: row.label === "Margin" && margin !== null ? (margin >= 40 ? "#10b981" : margin >= 20 ? "#f59e0b" : "#ef4444") : "#fff", fontWeight: 600, textAlign: "right", maxWidth: "60%" }}>{row.value}</span>
            </div>
          ))}
          {l.conditionNotes && (
            <div style={{ padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", fontWeight: 700, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.8 }}>Condition Notes</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", lineHeight: 1.6 }}>{l.conditionNotes}</div>
            </div>
          )}
          {l.compatibilityNotes && (
            <div style={{ padding: "12px 16px" }}>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", fontWeight: 700, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.8 }}>Compatibility Notes</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", lineHeight: 1.6 }}>{l.compatibilityNotes}</div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button onClick={() => navigate(`/admin/create-listing/${l._id}`)} style={{ ...btnStyle }}>Edit Listing →</button>
          <button onClick={() => deleteListing(l._id)} style={{ width: "100%", padding: "14px", border: "1px solid #fecaca", borderRadius: 14, background: "transparent", color: "#DC2626", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>Delete Listing</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "16px 16px 100px" }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: "#9ca3af" }}>ELEVEN</div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <h1 style={{ margin: "4px 0 0", fontSize: 28, fontWeight: 800, letterSpacing: -1, color: "#0f0f17" }}>Listings</h1>
          <button onClick={() => navigate("/admin/create-listing")} style={{ ...btnStyle, width: "auto", padding: "10px 16px", fontSize: 13 }}>+ Add</button>
        </div>
        <input style={inputStyle} placeholder="Search listings..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <Section>
        {loading && <div style={{ padding: "30px", textAlign: "center", color: "#9ca3af" }}>Loading...</div>}
        {!loading && filtered.length === 0 && <div style={{ padding: "30px", textAlign: "center", color: "#9ca3af" }}>No listings found</div>}
        {!loading && filtered.map(l => {
          const nv = l.variants?.find(v => v.condition === "new");
          const uv = l.variants?.find(v => v.condition === "used");
          const stock = l.unitsAvailable ?? 0;
          return (
            <div key={l._id} onClick={() => setSelected(l)} style={{ display: "flex", gap: 12, padding: "12px 16px", borderBottom: "1px solid #f9fafb", alignItems: "center", cursor: "pointer" }}>
              <img src={l.images?.[0] || ""} alt={l.title} style={{ width: 52, height: 52, borderRadius: 10, objectFit: "contain", background: "#f3f4f6", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#0f0f17", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.title}</div>
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{l.partNumber && `#${l.partNumber}`} {l.category}</div>
                <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                  {nv && <span style={{ fontSize: 11, fontWeight: 700, background: "#e0f2fe", color: "#0369a1", padding: "2px 6px", borderRadius: 999 }}>New ${nv.price}</span>}
                  {uv && <span style={{ fontSize: 11, fontWeight: 700, background: "#f3f4f6", color: "#6b7280", padding: "2px 6px", borderRadius: 999 }}>Used ${uv.price}</span>}
                  <span style={{ fontSize: 11, fontWeight: 700, color: stock <= 0 ? "#DC2626" : stock <= 3 ? "#B45309" : "#15803D", padding: "2px 6px", borderRadius: 999, background: stock <= 0 ? "#FFF0F0" : stock <= 3 ? "#FFF7E6" : "#EDFDF4" }}>{stock} units</span>
                </div>
              </div>
              <span style={{ color: "#d1d5db", fontSize: 18 }}>›</span>
            </div>
          );
        })}
      </Section>
    </div>
  );
}
// ── ANALYTICS ────────────────────────────────────────────────────
function AnalyticsTab() {
  const [range, setRange] = useState("weekly");
  const [summary, setSummary] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [events, setEvents] = useState([]);
  const RANGES = [{ key: "daily", label: "Today" }, { key: "weekly", label: "Week" }, { key: "monthly", label: "Month" }, { key: "all", label: "All" }];

  useEffect(() => {
  Promise.all([
      fetch(`${API}/api/analytics/summary?range=${range}`, { headers: authHeaders() }).then(r => r.json()),
      fetch(`${API}/api/analytics/funnel?range=${range}`, { headers: authHeaders() }).then(r => r.json()),
      fetch(`${API}/api/analytics/top-products?range=${range}`, { headers: authHeaders() }).then(r => r.json()),
      fetch(`${API}/api/analytics/events?range=${range}&limit=20`, { headers: authHeaders() }).then(r => r.json()),
    ])
    
    .then(([s, f, t, e]) => { setSummary(s); setFunnel(f.funnel || []); setTopProducts(t.topProducts || []); setEvents(e.events || []); }).catch(console.error);
  }, [range]);

  const metrics = summary ? [
    { label: "Visits",    value: summary.totalVisits,        color: "#6366f1" },
    { label: "Views",     value: summary.totalProductViews,  color: "#0ea5e9" },
    { label: "Cart",      value: summary.totalAddToCart,     color: "#f59e0b" },
    { label: "Checkout",  value: summary.totalCheckouts,     color: "#f97316" },
    { label: "Orders",    value: summary.totalOrders,        color: "#10b981" },
    { label: "Revenue",   value: `$${Number(summary.revenue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`, color: "#8b5cf6" },
    { label: "Conv. %",   value: `${summary.conversionRate}%`, color: "#ec4899" },
  ] : [];

  const maxFunnel = funnel[0]?.count || 1;

  return (
    <div style={{ padding: "16px 16px 100px" }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: "#9ca3af" }}>ELEVEN</div>
        <h1 style={{ margin: "4px 0 12px", fontSize: 28, fontWeight: 800, letterSpacing: -1, color: "#0f0f17" }}>Analytics</h1>
        <div style={{ display: "flex", gap: 6 }}>
          {RANGES.map(r => (
            <button key={r.key} onClick={() => setRange(r.key)} style={{ flex: 1, padding: "8px 0", borderRadius: 10, border: "1px solid", borderColor: range === r.key ? "#0f0f17" : "#e5e7eb", background: range === r.key ? "#0f0f17" : "transparent", color: range === r.key ? "#fff" : "#6b7280", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 8 }}>
        {metrics.slice(0, 4).map(m => (
          <div key={m.label} style={{ background: "#fff", borderRadius: 12, padding: "12px 8px", textAlign: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: m.color }} />
            <div style={{ fontSize: 18, fontWeight: 900, color: m.color, marginTop: 4 }}>{m.value}</div>
            <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 700 }}>{m.label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 16 }}>
        {metrics.slice(4).map(m => (
          <div key={m.label} style={{ background: "#fff", borderRadius: 12, padding: "12px 8px", textAlign: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: m.color }} />
            <div style={{ fontSize: 18, fontWeight: 900, color: m.color, marginTop: 4 }}>{m.value}</div>
            <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 700 }}>{m.label}</div>
          </div>
        ))}
      </div>

      <Section title="Conversion Funnel">
        {funnel.map(step => (
          <div key={step.stage} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderBottom: "1px solid #f9fafb" }}>
            <div style={{ fontSize: 12, color: "#374151", fontWeight: 600, width: 90, flexShrink: 0 }}>{step.stage}</div>
            <div style={{ flex: 1, height: 8, background: "#f3f4f6", borderRadius: 999, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${Math.round((step.count / maxFunnel) * 100)}%`, background: step.color || "#6366f1", borderRadius: 999 }} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 800, color: step.color || "#6366f1", width: 40, textAlign: "right" }}>{step.count}</div>
          </div>
        ))}
      </Section>

      <Section title="Top Products">
        {topProducts.length === 0 && <div style={{ padding: "20px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>No data yet</div>}
        {topProducts.map((p, i) => (
          <div key={p._id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderBottom: "1px solid #f9fafb" }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#d1d5db", width: 20 }}>#{i + 1}</div>
            <div style={{ flex: 1, fontSize: 13, color: "#374151", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title || p._id}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#6366f1" }}>{p.views}</div>
          </div>
        ))}
      </Section>

      <Section title="Activity Feed">
        {events.map((ev, i) => {
          const cfg = EVENT_CONFIG[ev.eventType] || { label: ev.eventType, color: "#888", bg: "#f5f5f5" };
          return (
            <div key={ev._id || i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderBottom: "1px solid #f9fafb" }}>
              <span style={{ background: cfg.bg, color: cfg.color, fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 999, whiteSpace: "nowrap" }}>{cfg.label}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
  <div style={{ fontSize: 12, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
    {ev.listingTitle || ev.metadata?.orderNumber || ""}
  </div>
  {ev.eventType === "checkout_started" && ev.metadata?.items?.length > 0 && (
    <div style={{ marginTop: 3, display: "flex", flexDirection: "column", gap: 2 }}>
      {ev.metadata.items.map((item, idx) => (
        <div key={idx} style={{ fontSize: 11, color: "#6b7280", display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontWeight: 700, color: "#f97316" }}>×{item.qty}</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 160 }}>{item.title}</span>
          <span style={{ fontWeight: 600, color: "#374151", flexShrink: 0 }}>${Number(item.price).toFixed(2)}</span>
        </div>
      ))}
    </div>
  )}
</div>
              <span style={{ fontSize: 11, color: "#9ca3af" }}>{timeAgo(ev.timestamp)}</span>
            </div>
          );
        })}
      </Section>
    </div>
  );
}

// ── CUSTOMERS ────────────────────────────────────────────────────
function CustomersScreen({ onBack }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);

  useEffect(() => {
   fetch(`${API}/auth/users`, { headers: authHeaders() }).then(r => r.json()).then(d => setCustomers(d.users || [])).finally(() => setLoading(false));
  }, []);

  const openCustomer = async c => {
    setSelected(c);
    const d = await fetch(`${API}/api/orders?userId=${c.id}&email=${encodeURIComponent(c.email)}`).then(r => r.json());
    setCustomerOrders(d.orders || []);
  };

  const filtered = customers.filter(c => !search || `${c.firstName} ${c.lastName} ${c.email}`.toLowerCase().includes(search.toLowerCase()));

  if (selected) return (
    <div style={{ padding: "16px 16px 100px" }}>
      <button onClick={() => setSelected(null)} style={{ background: "#f3f4f6", border: "none", borderRadius: 10, padding: "8px 14px", fontSize: 14, fontWeight: 600, cursor: "pointer", marginBottom: 16 }}>← Back</button>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
        <div style={{ width: 52, height: 52, borderRadius: "50%", background: "linear-gradient(135deg,#6366f1,#8b5cf6)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 800 }}>
          {`${selected.firstName?.[0] || ""}${selected.lastName?.[0] || ""}`.toUpperCase()}
        </div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#0f0f17" }}>{selected.firstName} {selected.lastName}</div>
          <div style={{ fontSize: 13, color: "#9ca3af" }}>{selected.email}</div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
        {[{ label: "Orders", value: selected.orderCount }, { label: "Spent", value: `$${Number(selected.totalSpend || 0).toFixed(0)}` }, { label: "Last Order", value: timeAgo(selected.lastOrderDate) }].map(s => (
          <div key={s.label} style={{ background: "#fff", borderRadius: 12, padding: "12px 8px", textAlign: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#0f0f17" }}>{s.value}</div>
            <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <Section title="Account Info">
        <InfoRow label="Member Since" value={new Date(selected.createdAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} />
        <InfoRow label="Role" value={selected.role} />
        <InfoRow label="Email" value={selected.email} link={`mailto:${selected.email}`} />
      </Section>
      <Section title="Order History">
        {customerOrders.length === 0 && <div style={{ padding: "16px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>No orders yet</div>}
        {customerOrders.map(o => (
          <div key={o._id} style={{ display: "flex", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid #f9fafb" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0f0f17" }}>{o.orderNumber}</div>
              <div style={{ fontSize: 12, color: "#9ca3af" }}>{new Date(o.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <Badge status={o.fulfillmentStatus || "order_placed"} map={STATUS_COLORS} />
              <div style={{ fontSize: 14, fontWeight: 800, color: "#0f0f17", marginTop: 3 }}>${Number(o.total || 0).toFixed(2)}</div>
            </div>
          </div>
        ))}
      </Section>
    </div>
  );

  return (
    <div style={{ padding: "16px 16px 100px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <button onClick={onBack} style={{ background: "#f3f4f6", border: "none", borderRadius: 10, padding: "8px 14px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>←</button>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: -0.5, color: "#0f0f17" }}>Customers</h1>
      </div>
      <input style={{ ...inputStyle, marginBottom: 12 }} placeholder="Search customers..." value={search} onChange={e => setSearch(e.target.value)} />
      <Section>
        {loading && <div style={{ padding: "30px", textAlign: "center", color: "#9ca3af" }}>Loading...</div>}
        {!loading && filtered.map(c => (
          <div key={c.id} onClick={() => openCustomer(c)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderBottom: "1px solid #f9fafb", cursor: "pointer" }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", background: "linear-gradient(135deg,#6366f1,#8b5cf6)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, flexShrink: 0 }}>
              {`${c.firstName?.[0] || ""}${c.lastName?.[0] || ""}`.toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#0f0f17" }}>{c.firstName} {c.lastName}</div>
              <div style={{ fontSize: 12, color: "#9ca3af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.email}</div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#8b5cf6" }}>${Number(c.totalSpend || 0).toFixed(0)}</div>
              <div style={{ fontSize: 11, color: "#9ca3af" }}>{c.orderCount} orders</div>
            </div>
            <span style={{ color: "#d1d5db", fontSize: 18 }}>›</span>
          </div>
        ))}
      </Section>
    </div>
  );
}

// ── INVENTORY ────────────────────────────────────────────────────
function InventoryScreen({ onBack }) {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const LOW = 3;

  useEffect(() => {
    fetch(`${API}/api/listings`).then(r => r.json()).then(d => setListings(Array.isArray(d) ? d : [])).finally(() => setLoading(false));
  }, []);

  const rows = listings.map(l => {
    const nv = l.variants?.find(v => v.condition === "new");
    const uv = l.variants?.find(v => v.condition === "used");
    const total = (nv?.stock ?? 0) + (uv?.stock ?? 0) + (!nv && !uv ? (l.unitsAvailable ?? 0) : 0);
    return { _id: l._id, title: l.title, partNumber: l.partNumber, category: l.category, image: l.images?.[0] || "", newStock: nv?.stock ?? null, usedStock: uv?.stock ?? null, fallback: !nv && !uv ? (l.unitsAvailable ?? 0) : null, total };
  });

  const outOfStock = rows.filter(r => r.total <= 0).length;
  const lowStock = rows.filter(r => r.total > 0 && r.total <= LOW).length;

  const filtered = rows.filter(r => {
    const ms = !search || r.title?.toLowerCase().includes(search.toLowerCase()) || r.partNumber?.toLowerCase().includes(search.toLowerCase());
    const mf = filterStatus === "all" || (filterStatus === "out" && r.total <= 0) || (filterStatus === "low" && r.total > 0 && r.total <= LOW) || (filterStatus === "in" && r.total > LOW);
    return ms && mf;
  });

  return (
    <div style={{ padding: "16px 16px 100px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <button onClick={onBack} style={{ background: "#f3f4f6", border: "none", borderRadius: 10, padding: "8px 14px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>←</button>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: -0.5, color: "#0f0f17" }}>Inventory</h1>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginBottom: 12 }}>
        {[{ label: "Products", value: rows.length, color: "#0f0f17" }, { label: "Units", value: rows.reduce((s, r) => s + r.total, 0), color: "#0f0f17" }, { label: "Low", value: lowStock, color: "#B45309" }, { label: "Out", value: outOfStock, color: "#DC2626" }].map(s => (
          <div key={s.label} style={{ background: "#fff", borderRadius: 12, padding: "10px 8px", textAlign: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize: 20, fontWeight: 900, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 700 }}>{s.label}</div>
          </div>
        ))}
      </div>
      {(outOfStock > 0 || lowStock > 0) && (
        <div style={{ marginBottom: 12 }}>
          {outOfStock > 0 && <div style={{ padding: "10px 14px", background: "#FFF0F0", border: "1px solid #FECACA", borderRadius: 12, fontSize: 13, color: "#991B1B", marginBottom: 8 }}>⚠ {outOfStock} product{outOfStock !== 1 ? "s" : ""} out of stock</div>}
          {lowStock > 0 && <div style={{ padding: "10px 14px", background: "#FFF7E6", border: "1px solid #FDE68A", borderRadius: 12, fontSize: 13, color: "#92400E" }}>↓ {lowStock} product{lowStock !== 1 ? "s" : ""} running low</div>}
        </div>
      )}
      <input style={{ ...inputStyle, marginBottom: 10 }} placeholder="Search inventory..." value={search} onChange={e => setSearch(e.target.value)} />
      <div style={{ display: "flex", gap: 6, marginBottom: 14, overflowX: "auto", scrollbarWidth: "none", paddingBottom: 4 }}>
        {[{ key: "all", label: "All" }, { key: "out", label: "Out of Stock" }, { key: "low", label: "Low Stock" }, { key: "in", label: "In Stock" }].map(f => (
          <button key={f.key} onClick={() => setFilterStatus(f.key)} style={{ flexShrink: 0, padding: "6px 12px", borderRadius: 999, border: "1px solid", borderColor: filterStatus === f.key ? "#0f0f17" : "#e5e7eb", background: filterStatus === f.key ? "#0f0f17" : "transparent", color: filterStatus === f.key ? "#fff" : "#6b7280", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            {f.label}
          </button>
        ))}
      </div>
      <Section>
        {loading && <div style={{ padding: "30px", textAlign: "center", color: "#9ca3af" }}>Loading...</div>}
        {!loading && filtered.map(row => {
          const stockColor = row.total <= 0 ? "#DC2626" : row.total <= LOW ? "#B45309" : "#15803D";
          return (
            <div key={row._id} style={{ display: "flex", gap: 12, padding: "12px 16px", borderBottom: "1px solid #f9fafb", alignItems: "center", background: row.total <= 0 ? "#fff5f5" : row.total <= LOW ? "#fffbeb" : "transparent" }}>
              {row.image ? <img src={row.image} alt={row.title} style={{ width: 44, height: 44, borderRadius: 8, objectFit: "contain", background: "#f3f4f6", flexShrink: 0 }} /> : <div style={{ width: 44, height: 44, borderRadius: 8, background: "#f3f4f6", flexShrink: 0 }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#0f0f17", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.title}</div>
                <div style={{ fontSize: 11, color: "#9ca3af" }}>{row.partNumber && `#${row.partNumber}`} {row.category}</div>
                <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                  {row.newStock !== null && <span style={{ fontSize: 11, background: "#e0f2fe", color: "#0369a1", padding: "2px 6px", borderRadius: 999, fontWeight: 700 }}>New: {row.newStock}</span>}
                  {row.usedStock !== null && <span style={{ fontSize: 11, background: "#f3f4f6", color: "#6b7280", padding: "2px 6px", borderRadius: 999, fontWeight: 700 }}>Used: {row.usedStock}</span>}
                  {row.fallback !== null && <span style={{ fontSize: 11, background: "#f3f4f6", color: "#6b7280", padding: "2px 6px", borderRadius: 999, fontWeight: 700 }}>Stock: {row.fallback}</span>}
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: stockColor }}>{row.total}</div>
                <button onClick={() => navigate(`/admin/create-listing/${row._id}`)} style={{ fontSize: 11, padding: "4px 10px", border: "1px solid #e5e7eb", borderRadius: 8, background: "transparent", cursor: "pointer", color: "#374151", fontWeight: 600, marginTop: 4 }}>Edit</button>
              </div>
            </div>
          );
        })}
      </Section>
    </div>
  );
}

// ── MESSAGES ─────────────────────────────────────────────────────
function MessagesScreen({ onBack }) {
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
  fetch(`${API}/api/messages`, { headers: authHeaders() }).then(r => r.json()).then(d => { setMessages(d.messages || []); setUnread(d.unread || 0); }).finally(() => setLoading(false));
  }, []);

  const openMessage = async msg => {
    setSelected(msg);
    if (!msg.read) {
    await fetch(`${API}/api/messages/${msg._id}/read`, { method: "PATCH", headers: authHeaders() });
      setMessages(prev => prev.map(m => m._id === msg._id ? { ...m, read: true } : m));
      setUnread(prev => Math.max(0, prev - 1));
    }
  };

  const deleteMessage = async id => {
await fetch(`${API}/api/messages/${id}`, { method: "DELETE", headers: authHeaders() });
    setMessages(prev => prev.filter(m => m._id !== id));
    if (selected?._id === id) setSelected(null);
  };

  const filtered = messages.filter(m => filter === "all" || (filter === "unread" && !m.read) || (filter === "read" && m.read));

  if (selected) return (
    <div style={{ padding: "16px 16px 100px" }}>
      <button onClick={() => setSelected(null)} style={{ background: "#f3f4f6", border: "none", borderRadius: 10, padding: "8px 14px", fontSize: 14, fontWeight: 600, cursor: "pointer", marginBottom: 16 }}>← Back</button>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div style={{ width: 48, height: 48, borderRadius: "50%", background: "linear-gradient(135deg,#6366f1,#8b5cf6)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 800 }}>{selected.name?.[0]?.toUpperCase()}</div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#0f0f17" }}>{selected.name}</div>
          {selected.company && <div style={{ fontSize: 13, color: "#9ca3af" }}>{selected.company}</div>}
          <div style={{ fontSize: 12, color: "#9ca3af" }}>{new Date(selected.createdAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
        </div>
      </div>
      <Section title="Contact">
        <InfoRow label="Email" value={selected.email} link={`mailto:${selected.email}`} />
        {selected.company && <InfoRow label="Company" value={selected.company} />}
        {selected.phone && <InfoRow label="Phone" value={selected.phone} link={`tel:${selected.phone}`} />}
      </Section>
      <Section title="Message">
        <div style={{ padding: "14px 16px", fontSize: 14, color: "#374151", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{selected.message}</div>
      </Section>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <a href={`mailto:${selected.email}?subject=Re: Your ELEVEN Inquiry`} style={{ ...btnStyle, textAlign: "center", textDecoration: "none", display: "block" }}>✉ Reply via Email</a>
        <button onClick={() => deleteMessage(selected._id)} style={{ width: "100%", padding: "14px", border: "1px solid #fecaca", borderRadius: 14, background: "transparent", color: "#DC2626", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>Delete Message</button>
      </div>
    </div>
  );

  return (
    <div style={{ padding: "16px 16px 100px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <button onClick={onBack} style={{ background: "#f3f4f6", border: "none", borderRadius: 10, padding: "8px 14px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>←</button>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: -0.5, color: "#0f0f17" }}>
          Messages {unread > 0 && <span style={{ fontSize: 13, background: "#DC2626", color: "#fff", padding: "2px 8px", borderRadius: 999, marginLeft: 6 }}>{unread}</span>}
        </h1>
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {["all", "unread", "read"].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ flex: 1, padding: "8px 0", borderRadius: 10, border: "1px solid", borderColor: filter === f ? "#0f0f17" : "#e5e7eb", background: filter === f ? "#0f0f17" : "transparent", color: filter === f ? "#fff" : "#6b7280", fontSize: 13, fontWeight: 600, cursor: "pointer", textTransform: "capitalize" }}>{f}</button>
        ))}
      </div>
      <Section>
        {loading && <div style={{ padding: "30px", textAlign: "center", color: "#9ca3af" }}>Loading...</div>}
        {!loading && filtered.length === 0 && <div style={{ padding: "30px", textAlign: "center", color: "#9ca3af" }}>No messages</div>}
        {!loading && filtered.map(msg => (
          <div key={msg._id} onClick={() => openMessage(msg)} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 16px", borderBottom: "1px solid #f9fafb", cursor: "pointer", background: !msg.read ? "#fafafe" : "transparent" }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", background: !msg.read ? "linear-gradient(135deg,#6366f1,#8b5cf6)" : "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <span style={{ color: !msg.read ? "#fff" : "#9ca3af", fontSize: 14, fontWeight: 800 }}>{msg.name?.[0]?.toUpperCase()}</span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 14, fontWeight: msg.read ? 500 : 800, color: "#0f0f17" }}>{msg.name}</span>
                <span style={{ fontSize: 11, color: "#9ca3af" }}>{timeAgo(msg.createdAt)}</span>
              </div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1 }}>{msg.email}</div>
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: msg.read ? 400 : 600 }}>{msg.message?.slice(0, 70)}{msg.message?.length > 70 ? "..." : ""}</div>
            </div>
            {!msg.read && <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#6366f1", marginTop: 6, flexShrink: 0 }} />}
          </div>
        ))}
      </Section>
    </div>
  );
}

// ── RETURNS ──────────────────────────────────────────────────────
function ReturnsScreen({ onBack }) {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [adminNote, setAdminNote] = useState("");
  const [creditAmt, setCreditAmt] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [customerCredit, setCustomerCredit] = useState(null);

  useEffect(() => { fetchReturns(); }, []);
  useEffect(() => { if (selected) { setAdminNote(selected.adminNote || ""); setCreditAmt(""); fetchCredit(selected.email); } }, [selected]);

  const fetchReturns = async () => {
    const d = await fetch(`${API}/api/returns`, { headers: { Authorization: `Bearer ${token()}` } }).then(r => r.json());
    setReturns(d.returns || []);
    setLoading(false);
  };

  const fetchCredit = async email => {
    const d = await fetch(`${API}/auth/users`, { headers: { Authorization: `Bearer ${token()}` } }).then(r => r.json());
    const u = (d.users || []).find(u => u.email === email);
    setCustomerCredit(u?.storeCredit ?? null);
  };

  const action = async (endpoint, body = {}) => {
    setActionLoading(true);
    const currentEmail = selected.email;
    const d = await fetch(`${API}/api/returns/${selected._id}/${endpoint}`, { method: "PATCH", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` }, body: JSON.stringify(body) }).then(r => r.json());
    if (d.success) {
      await fetchReturns();
      const upd = (await fetch(`${API}/api/returns`, { headers: { Authorization: `Bearer ${token()}` } }).then(r => r.json())).returns?.find(r => r._id === selected._id);
      if (upd) { setSelected(upd); await fetchCredit(currentEmail); }
    }
    setActionLoading(false);
  };

  const filtered = filter === "all" ? returns : returns.filter(r => r.status === filter);

  if (selected) return (
    <div style={{ padding: "16px 16px 100px" }}>
      <button onClick={() => setSelected(null)} style={{ background: "#f3f4f6", border: "none", borderRadius: 10, padding: "8px 14px", fontSize: 14, fontWeight: 600, cursor: "pointer", marginBottom: 16 }}>← Back</button>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#0f0f17" }}>{selected.returnNumber}</div>
          <div style={{ fontSize: 13, color: "#9ca3af" }}>Order {selected.orderNumber}</div>
        </div>
        <Badge status={selected.status} map={RETURN_STATUS} />
      </div>
      <Section title="Customer">
        <InfoRow label="Email" value={selected.email} link={`mailto:${selected.email}`} />
        <InfoRow label="Store Credit" value={customerCredit !== null ? `$${Number(customerCredit).toFixed(2)}` : "—"} />
        <InfoRow label="Submitted" value={new Date(selected.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} />
      </Section>
      <Section title="Return Details">
        <InfoRow label="Reason" value={selected.reason} />
        {selected.details && <div style={{ padding: "12px 16px", fontSize: 13, color: "#6b7280", background: "#f9fafb", lineHeight: 1.6 }}>{selected.details}</div>}
      </Section>
      <Section title="Internal Note">
        <div style={{ padding: "12px 16px" }}>
          <textarea style={{ width: "100%", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px", fontSize: 13, resize: "vertical", fontFamily: "inherit", boxSizing: "border-box", outline: "none" }} rows={3} value={adminNote} onChange={e => setAdminNote(e.target.value)} placeholder="Add note..." />
          <button onClick={() => action("note", { adminNote })} disabled={actionLoading} style={{ ...btnStyle, marginTop: 8, fontSize: 13, padding: "10px" }}>Save Note</button>
        </div>
      </Section>
      <Section title="Actions">
        <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
          {selected.status === "pending" && (
            <>
              <button onClick={() => action("approve", { adminNote })} disabled={actionLoading} style={{ ...btnStyle, background: "#3b82f6" }}>Approve</button>
              <button onClick={() => action("deny", { adminNote })} disabled={actionLoading} style={{ ...btnStyle, background: "#ef4444" }}>Deny</button>
            </>
          )}
          {selected.status === "approved" && <button onClick={() => action("label-sent")} disabled={actionLoading} style={{ ...btnStyle, background: "#8b5cf6" }}>Mark Label Sent</button>}
          {selected.status === "label_sent" && <button onClick={() => action("received")} disabled={actionLoading} style={{ ...btnStyle, background: "#06b6d4" }}>Mark Received</button>}
          {selected.status === "received" && (
            <div style={{ display: "flex", gap: 10 }}>
              <input type="number" placeholder="Credit amount ($)" value={creditAmt} onChange={e => setCreditAmt(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
              <button onClick={() => action("issue-credit", { amount: parseFloat(creditAmt) })} disabled={actionLoading || !creditAmt} style={{ ...btnStyle, background: "#10b981", width: "auto", padding: "12px 16px" }}>Issue</button>
            </div>
          )}
          {selected.status === "credited" && <div style={{ fontSize: 14, color: "#10b981", fontWeight: 600 }}>✓ ${selected.storeCreditAmount?.toFixed(2)} credited</div>}
          {selected.status === "denied" && <div style={{ fontSize: 14, color: "#ef4444", fontWeight: 600 }}>✕ Return denied</div>}
        </div>
      </Section>
    </div>
  );

  return (
    <div style={{ padding: "16px 16px 100px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <button onClick={onBack} style={{ background: "#f3f4f6", border: "none", borderRadius: 10, padding: "8px 14px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>←</button>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: -0.5, color: "#0f0f17" }}>
          Returns {returns.filter(r => r.status === "pending").length > 0 && <span style={{ fontSize: 13, background: "#f59e0b", color: "#000", padding: "2px 8px", borderRadius: 999, marginLeft: 6 }}>{returns.filter(r => r.status === "pending").length} Pending</span>}
        </h1>
      </div>
      <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4, marginBottom: 12, scrollbarWidth: "none" }}>
        {["all", "pending", "approved", "denied", "label_sent", "received", "credited"].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ flexShrink: 0, padding: "6px 12px", borderRadius: 999, border: "1px solid", borderColor: filter === f ? "#0f0f17" : "#e5e7eb", background: filter === f ? "#0f0f17" : "transparent", color: filter === f ? "#fff" : "#6b7280", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            {f === "all" ? "All" : RETURN_STATUS[f]?.label}
          </button>
        ))}
      </div>
      <Section>
        {loading && <div style={{ padding: "30px", textAlign: "center", color: "#9ca3af" }}>Loading...</div>}
        {!loading && filtered.length === 0 && <div style={{ padding: "30px", textAlign: "center", color: "#9ca3af" }}>No returns found</div>}
        {!loading && filtered.map(ret => (
          <div key={ret._id} onClick={() => setSelected(ret)} style={{ padding: "14px 16px", borderBottom: "1px solid #f9fafb", cursor: "pointer" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#0f0f17" }}>{ret.returnNumber}</span>
              <Badge status={ret.status} map={RETURN_STATUS} />
            </div>
            <div style={{ fontSize: 12, color: "#9ca3af" }}>Order {ret.orderNumber} · {ret.email}</div>
            <div style={{ fontSize: 13, color: "#374151", marginTop: 4 }}>{ret.reason}</div>
          </div>
        ))}
      </Section>
    </div>
  );
}

// ── MORE TAB ─────────────────────────────────────────────────────
function MoreTab() {
  const [subTab, setSubTab] = useState(null);
  if (subTab === "customers") return <CustomersScreen onBack={() => setSubTab(null)} />;
  if (subTab === "inventory") return <InventoryScreen onBack={() => setSubTab(null)} />;
  if (subTab === "messages") return <MessagesScreen onBack={() => setSubTab(null)} />;
  if (subTab === "returns") return <ReturnsScreen onBack={() => setSubTab(null)} />;

  const items = [
    { key: "customers", icon: "👤", label: "Customers",  sub: "View and manage customers" },
    { key: "inventory", icon: "📦", label: "Inventory",  sub: "Stock levels and alerts" },
    { key: "messages",  icon: "✉️", label: "Messages",   sub: "Customer inquiries" },
    { key: "returns",   icon: "↩️", label: "Returns",    sub: "Return requests and credits" },
  ];

  return (
    <div style={{ padding: "16px 16px 100px" }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: "#9ca3af" }}>ELEVEN</div>
        <h1 style={{ margin: "4px 0 0", fontSize: 28, fontWeight: 800, letterSpacing: -1, color: "#0f0f17" }}>More</h1>
      </div>
      <Section>
        {items.map(item => (
          <div key={item.key} onClick={() => setSubTab(item.key)} style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 16px", borderBottom: "1px solid #f9fafb", cursor: "pointer" }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>{item.icon}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0f0f17" }}>{item.label}</div>
              <div style={{ fontSize: 13, color: "#9ca3af", marginTop: 2 }}>{item.sub}</div>
            </div>
            <span style={{ color: "#d1d5db", fontSize: 20 }}>›</span>
          </div>
        ))}
      </Section>
    </div>
  );
}

// ── Bottom Tab Bar ────────────────────────────────────────────────
const TABS = [
  { key: "dashboard", label: "Dashboard", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg> },
  { key: "orders",    label: "Orders",    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg> },
  { key: "listings",  label: "Listings",  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg> },
  { key: "analytics", label: "Analytics", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg> },
  { key: "more",      label: "More",      icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg> },
];

export default function AdminMobile() {
  const [tab, setTab] = useState("dashboard");

  return (
    <div style={{ background: "#F4F5F7", minHeight: "100vh", fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui", overflowX: "hidden" }}>

      <div style={{ overflowY: "auto" }}>
        {tab === "dashboard"  && <DashboardTab />}
        {tab === "orders"     && <OrdersTab />}
        {tab === "listings"   && <ListingsTab />}
        {tab === "analytics"  && <AnalyticsTab />}
        {tab === "more"       && <MoreTab />}
      </div>

      {/* Bottom Tab Bar */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "rgba(255,255,255,0.95)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", borderTop: "1px solid rgba(0,0,0,0.08)", display: "flex", zIndex: 1000, paddingBottom: "env(safe-area-inset-bottom)" }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "10px 0 8px", border: "none", background: "transparent", cursor: "pointer", color: tab === t.key ? "#0f0f17" : "#9CA3AF", transition: "color 0.15s", position: "relative" }}>
            <div style={{ color: tab === t.key ? "#0f0f17" : "#9CA3AF" }}>{t.icon}</div>
            <span style={{ fontSize: 10, fontWeight: tab === t.key ? 800 : 600, letterSpacing: 0.2 }}>{t.label}</span>
            {tab === t.key && <div style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: 20, height: 2, borderRadius: 999, background: "#0f0f17" }} />}
          </button>
        ))}
      </div>
    </div>
  );
}